interface CircuitRingsProps {
  size: number;
  isActive: boolean;
  canPlay: boolean; // Sessions available - rotate even when not active
}

export function CircuitRings({ size, isActive, canPlay }: CircuitRingsProps) {
  const center = size / 2;
  const ringRadii = [size * 0.38, size * 0.44, size * 0.5];

  // Binary pattern for text paths
  const binaryPattern = '01001010 11010010 00110101 10101001 ';

  // Should animate (rotate) when active OR when sessions available
  const shouldAnimate = isActive || canPlay;

  // Get opacity based on state: active > canPlay > disabled
  const getOpacity = (activeVal: number, canPlayVal: number, disabledVal: number) => {
    if (isActive) return activeVal;
    if (canPlay) return canPlayVal;
    return disabledVal;
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="absolute pointer-events-none"
      style={{ willChange: 'transform' }}
    >
      <defs>
        {/* Define circular text paths */}
        {ringRadii.map((r, i) => (
          <path
            key={`path-${i}`}
            id={`ring-path-${i}`}
            d={`M ${center},${center} m -${r},0 a ${r},${r} 0 1,1 ${r * 2},0 a ${r},${r} 0 1,1 -${r * 2},0`}
            fill="none"
          />
        ))}
      </defs>

      {/* Inner ring - dashed, slow rotation */}
      <g className={shouldAnimate ? 'animate-ring-rotate-slow' : ''} style={{ transformOrigin: 'center' }}>
        <circle
          cx={center}
          cy={center}
          r={ringRadii[0]}
          fill="none"
          stroke={`rgba(0, 255, 255, ${getOpacity(0.6, 0.45, 0.2)})`}
          strokeWidth="2"
          strokeDasharray="8 4 2 4"
        />
        {/* Circuit nodes on inner ring */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
          const rad = (angle * Math.PI) / 180;
          const x = center + ringRadii[0] * Math.cos(rad);
          const y = center + ringRadii[0] * Math.sin(rad);
          return (
            <circle
              key={`node-inner-${angle}`}
              cx={x}
              cy={y}
              r="2.5"
              fill={`rgba(0, 255, 255, ${getOpacity(0.9, 0.7, 0.3)})`}
            />
          );
        })}
      </g>

      {/* Middle ring - binary text, reverse rotation */}
      <g className={shouldAnimate ? 'animate-ring-rotate-reverse' : ''} style={{ transformOrigin: 'center' }}>
        <circle
          cx={center}
          cy={center}
          r={ringRadii[1]}
          fill="none"
          stroke={`rgba(0, 255, 255, ${getOpacity(0.45, 0.3, 0.12)})`}
          strokeWidth="1.5"
        />
        <text
          fill={`rgba(0, 255, 255, ${getOpacity(0.5, 0.35, 0.2)})`}
          fontSize="6"
          fontFamily="monospace"
        >
          <textPath href="#ring-path-1" startOffset="0%">
            {binaryPattern.repeat(8)}
          </textPath>
        </text>
      </g>

      {/* Outer ring - dashed with longer gaps */}
      <g className={shouldAnimate ? 'animate-ring-rotate-slow' : ''} style={{ transformOrigin: 'center' }}>
        <circle
          cx={center}
          cy={center}
          r={ringRadii[2]}
          fill="none"
          stroke={`rgba(0, 255, 255, ${getOpacity(0.55, 0.4, 0.15)})`}
          strokeWidth="2"
          strokeDasharray="20 10 5 10"
        />
        {/* Corner markers on outer ring */}
        {[30, 60, 120, 150, 210, 240, 300, 330].map((angle) => {
          const rad = (angle * Math.PI) / 180;
          const x = center + ringRadii[2] * Math.cos(rad);
          const y = center + ringRadii[2] * Math.sin(rad);
          return (
            <rect
              key={`marker-${angle}`}
              x={x - 2}
              y={y - 2}
              width="4"
              height="4"
              fill={`rgba(0, 255, 255, ${getOpacity(0.85, 0.65, 0.3)})`}
              transform={`rotate(${angle + 45}, ${x}, ${y})`}
            />
          );
        })}
      </g>
    </svg>
  );
}
